<template>
  <v-container class="certificados-page py-4 px-4 px-md-6" fluid>
    <section class="cert-hero mb-3">
      <v-row align="center" no-gutters>
        <v-col cols="12" md="8">
          <div class="hero-kicker">
            <v-icon small color="white" class="mr-1">mdi-view-dashboard-outline</v-icon>
            Academico / Certificados
          </div>
          <h1 class="hero-title">Operacion de certificados</h1>
          <p class="hero-subtitle mb-0">
            Genera lotes por ciudad, ajusta fondos y corrige certificados individuales antes de exportarlos.
          </p>
        </v-col>

        <v-col cols="12" md="4" class="text-md-right mt-4 mt-md-0">
          <div class="hero-actions">
            <v-chip small class="hero-chip mb-2">
              <v-icon left x-small>mdi-calendar-check-outline</v-icon>
              {{ ciclo ? ciclo.ciclo : 'Sin ciclo seleccionado' }}
            </v-chip>
            <v-btn
              rounded
              depressed
              color="white"
              class="hero-refresh"
              :loading="cargar"
              @click="buscar"
            >
              <v-icon left small color="primary">mdi-refresh</v-icon>
              Actualizar listado
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </section>

    <v-card class="cert-panel mb-4" flat>
      <v-tabs
        v-model="tabEscuela"
        class="school-tabs"
        background-color="transparent"
        color="primary"
        slider-size="4"
      >
        <v-tab>
          <v-icon left small>mdi-school-outline</v-icon>
          Certificados INBI
        </v-tab>
        <v-tab>
          <v-icon left small>mdi-certificate-outline</v-icon>
          Certificados FAST
        </v-tab>
      </v-tabs>

      <v-divider></v-divider>

      <v-card-text class="pa-4">
        <v-row dense class="filter-grid">
          <v-col cols="12" md="3">
            <v-autocomplete
              v-model="ciclo"
              :items="ciclosFiltrados"
              item-text="ciclo"
              return-object
              clearable
              dense
              outlined
              rounded
              prepend-inner-icon="mdi-calendar-range"
              label="Ciclo"
            ></v-autocomplete>
          </v-col>

          <v-col cols="12" md="3">
            <v-select
              v-model="tipo"
              :items="tiposCertificado"
              item-text="text"
              item-value="value"
              dense
              outlined
              rounded
              prepend-inner-icon="mdi-file-certificate-outline"
              label="Tipo de certificado"
            ></v-select>
          </v-col>

          <v-col cols="12" md="3">
            <v-select
              v-model="ciudad"
              :items="ciudades"
              clearable
              dense
              outlined
              rounded
              prepend-inner-icon="mdi-map-marker-outline"
              label="Ciudad para lote"
            ></v-select>
          </v-col>

          <v-col cols="12" md="3">
            <v-text-field
              v-model="search"
              dense
              outlined
              rounded
              prepend-inner-icon="mdi-magnify"
              label="Buscar alumno"
            ></v-text-field>
          </v-col>
        </v-row>

        <div class="stats-strip mb-3">
          <div class="metric-pill">
            <v-icon small color="primary">mdi-file-certificate-outline</v-icon>
            <span>Total</span>
            <strong>{{ totalCertificados }}</strong>
            <small>{{ escuelaTexto }} / {{ tipoTexto }}</small>
          </div>

          <div class="metric-pill metric-pending">
            <v-icon small color="red darken-2">mdi-clock-outline</v-icon>
            <span>Pendientes</span>
            <strong>{{ totalPendientes }}</strong>
          </div>

          <div class="metric-pill metric-exported">
            <v-icon small color="deep-purple darken-3">mdi-file-export-outline</v-icon>
            <span>Exportados</span>
            <strong>{{ totalExportados }}</strong>
          </div>

          <div class="metric-pill metric-selected">
            <v-icon small color="teal darken-2">mdi-checkbox-marked-circle-outline</v-icon>
            <span>Seleccionados</span>
            <strong>{{ totalSeleccionados }}</strong>
          </div>
        </div>

        <div class="notice-row mb-4">
          <v-alert
            dense
            text
            color="info"
            class="modern-alert notice-item mb-0"
          >
            <v-icon left small color="info">mdi-information-outline</v-icon>
            Todos arrancan como pendiente. Puedes generar lotes por ciudad, cambiar el fondo de la plantilla y editar certificados individuales antes de exportarlos.
          </v-alert>

          <v-card class="action-bar notice-item" flat>
            <div class="action-info">
              <v-icon color="info" small class="mr-2">mdi-printer-outline</v-icon>
              <strong>Exportacion de certificados</strong>
              <span>Usa seleccion manual o lote por ciudad.</span>
            </div>

            <div class="action-buttons">
              <v-btn
                rounded
                depressed
                small
                color="blue-grey darken-1"
                dark
                :disabled="!alumnosFiltrados.length"
                @click="descargarListaNombres"
              >
                <v-icon left small>mdi-text-box-check-outline</v-icon>
                Lista nombres
                <span class="ml-1">({{ selected.length || alumnosFiltrados.length }})</span>
              </v-btn>

              <v-btn
                rounded
                depressed
                small
                color="deep-purple darken-3"
                dark
                :disabled="!selected.length"
                :loading="generandoLote"
                @click="generarLotePDF"
              >
                <v-icon left small>mdi-file-pdf-box</v-icon>
                Lote
                <span v-if="selected.length" class="ml-1">({{ selected.length }})</span>
              </v-btn>

              <v-btn
                rounded
                depressed
                small
                color="teal darken-2"
                dark
                :disabled="!ciudad"
                :loading="generandoCiudad"
                @click="generarLoteCiudad"
              >
                <v-icon left small>mdi-city-variant-outline</v-icon>
                Ciudad
              </v-btn>
            </div>
          </v-card>
        </div>

        <v-expansion-panels class="template-panel mb-4" flat>
          <v-expansion-panel>
            <v-expansion-panel-header>
              <div>
                <div class="panel-title">
                  <v-icon color="primary" class="mr-2">mdi-palette-outline</v-icon>
                  Fondos y posiciones de plantilla
                </div>
                <div class="panel-subtitle">Selecciona la plantilla y cambia el fondo que se usara en los proximos PDFs.</div>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row dense>
                <v-col cols="12" md="4">
                  <v-select
                    v-model="plantillaSeleccionadaId"
                    :items="plantillas"
                    item-text="nombre_plantilla"
                    item-value="id"
                    dense
                    outlined
                    rounded
                    prepend-inner-icon="mdi-image-filter-center-focus"
                    label="Plantilla"
                  ></v-select>
                </v-col>

                <v-col cols="12" md="4">
                  <v-file-input
                    v-model="plantillaFile"
                    accept="image/png, image/jpeg, image/jpg, image/webp"
                    dense
                    outlined
                    rounded
                    prepend-icon=""
                    prepend-inner-icon="mdi-cloud-upload-outline"
                    label="Cambiar fondo"
                  ></v-file-input>
                </v-col>

                <v-col cols="12" md="4">
                  <v-btn
                    block
                    rounded
                    depressed
                    color="primary"
                    dark
                    large
                    :disabled="!plantillaSeleccionadaId"
                    :loading="guardandoPlantilla"
                    @click="guardarPlantilla"
                  >
                    <v-icon left>mdi-content-save-outline</v-icon>
                    Guardar plantilla
                  </v-btn>
                </v-col>

                <v-col cols="12" md="4" class="d-flex align-center">
                  <v-chip :color="plantillaForm.dinamica ? 'green' : 'blue-grey'" dark class="status-chip">
                    <v-icon left small>{{ plantillaForm.dinamica ? 'mdi-check-circle-outline' : 'mdi-image-outline' }}</v-icon>
                    {{ plantillaForm.dinamica ? 'Fondo cambiado' : 'Precargado' }}
                  </v-chip>
                </v-col>

                <v-col cols="12" md="6" v-if="plantillaForm.url">
                  <v-card class="preview-card" flat>
                    <v-img
                      :src="urlArchivo(plantillaForm.url)"
                      max-height="240"
                      contain
                    ></v-img>
                  </v-card>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-card class="table-shell" flat>
          <div class="table-header">
            <div>
              <div class="table-title">Certificados encontrados</div>
              <div class="table-subtitle">{{ totalCertificados }} registros en el listado actual</div>
            </div>
            <v-chip color="primary" text-color="white" small>
              {{ totalSeleccionados }} seleccionados
            </v-chip>
          </div>

          <v-data-table
            v-model="selected"
            :headers="headers"
            :items="alumnosFiltrados"
            :search="search"
            :loading="cargar"
            :footer-props="{ 'items-per-page-text': 'Filas por pagina' }"
            item-key="certificado_key"
            show-select
            class="cert-table elevation-0"
          >
            <template v-slot:item.escuela="{ item }">
              <v-chip small pill v-if="item.escuela == 1" color="#2EDAD5" text-color="#053b3b">INBI</v-chip>
              <v-chip small pill v-else color="#000D4A" dark>FAST</v-chip>
            </template>

            <template v-slot:item.estatus_operativo="{ item }">
              <v-chip small pill :color="colorEstatus(item.estatus_operativo)" dark>
                {{ item.estatus_operativo }}
              </v-chip>
            </template>

            <template v-slot:item.nombre_modificado="{ item }">
              <span class="font-weight-medium">{{ item.nombre_modificado || '-' }}</span>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-btn rounded x-small depressed color="primary" dark @click="editarCertificado(item)">
                <v-icon left x-small>mdi-pencil-outline</v-icon>
                Editar
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-card-text>
    </v-card>

    <v-dialog v-model="dialogEditar" max-width="560px">
      <v-card class="edit-dialog">
        <v-card-title class="dialog-title">
          <v-avatar color="primary" size="36" class="mr-3">
            <v-icon color="white" small>mdi-pencil-outline</v-icon>
          </v-avatar>
          Editar certificado
        </v-card-title>

        <v-card-text class="pt-2">
          <v-text-field
            v-model="certificadoEditado.nombre_modificado"
            dense
            outlined
            rounded
            prepend-inner-icon="mdi-account-edit-outline"
            label="Nombre para imprimir"
          ></v-text-field>

          <v-text-field
            :value="certificadoEditado.matricula"
            dense
            outlined
            rounded
            disabled
            prepend-inner-icon="mdi-card-account-details-outline"
            label="Matricula"
          ></v-text-field>
        </v-card-text>

        <v-card-actions class="px-6 pb-5">
          <v-spacer></v-spacer>
          <v-btn rounded text :disabled="guardandoNombre || generandoIndividual" @click="dialogEditar = false">Cancelar</v-btn>
          <v-btn rounded color="primary" text :loading="guardandoNombre" @click="guardarEdicionIndividual">Guardar</v-btn>
          <v-btn
            rounded
            depressed
            color="deep-purple darken-3"
            dark
            :loading="generandoIndividual"
            :disabled="guardandoNombre"
            @click="exportarIndividual"
          >
            <v-icon left small>mdi-file-export-outline</v-icon>
            Exportar individual
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <Alerta v-if="respuestaAlerta" :parametros="parametros"/>
    <carga v-if="cargar"/>
  </v-container>
</template>

<script>
  import Alerta from '@/components/alertas/Alerta.vue'
  import carga from '@/components/alertas/carga.vue'
  import XLSX from 'xlsx'

  export default {
    components: {
      Alerta,
      carga
    },

    data: () => ({
      tabEscuela: 0,
      tipo: 'fin_curso',
      ciclo: null,
      ciudad: null,
      ciudadesBase: ['MONTERREY', 'CDMX', 'GDL', 'FAST'],
      ciclos: [],
      alumnos: [],
      selected: [],
      search: '',
      cargar: false,
      buscarToken: 0,
      generandoLote: false,
      generandoCiudad: false,
      generandoIndividual: false,
      guardandoNombre: false,
      guardandoPlantilla: false,
      plantillas: [],
      plantillaSeleccionadaId: null,
      plantillaFile: null,
      plantillaForm: {
        nombre_y: null,
        nombre_font_size: null,
        firma_x: null,
        firma_y: null,
        firma_width: null,
        url: null,
        dinamica: false
      },
      dialogEditar: false,
      certificadoEditado: {},
      edicionesLocales: {},
      respuestaAlerta: false,
      parametros: {
        dialogError: false,
        mensaje: '',
        color: ''
      },
      headers: [
        { text: 'Matricula', value: 'matricula' },
        { text: 'Nombre en sistema', value: 'nombre_sistema' },
        { text: 'Nombre modificado', value: 'nombre_modificado' },
        { text: 'Sucursal', value: 'sucursal' },
        { text: 'Ciudad', value: 'ciudad' },
        { text: 'Ciclo', value: 'ciclo' },
        { text: 'Escuela', value: 'escuela' },
        { text: 'Estatus', value: 'estatus_operativo' },
        { text: 'Acciones', value: 'actions', sortable: false },
      ],
    }),

    computed: {
      escuela () {
        return this.tabEscuela === 0 ? 1 : 2
      },

      ciclosFiltrados () {
        const esFast = this.escuela === 2
        return this.ciclos.filter(ciclo => {
          const nombre = String(ciclo.ciclo || '')
          const tieneFe = nombre.search('FE') !== -1
          return esFast ? tieneFe : !tieneFe
        })
      },

      tiposCertificado () {
        const tipos = [
          { text: 'Fin de curso', value: 'fin_curso' },
          { text: 'Certificacion', value: 'certificacion' },
        ]

        if(this.escuela === 1){
          tipos.push({ text: 'Teens', value: 'teens' })
        }

        return tipos
      },

      ciudades () {
        const ciudadesAlumnos = this.alumnos.map(alumno => alumno.ciudad).filter(Boolean)
        return [...new Set(this.ciudadesBase.concat(ciudadesAlumnos))]
          .sort((a, b) => String(a).localeCompare(String(b)))
      },

      alumnosFiltrados () {
        if(!this.ciudad) return this.alumnos

        const ciudad = String(this.ciudad).trim().toUpperCase()
        return this.alumnos.filter(alumno => String(alumno.ciudad || '').trim().toUpperCase() === ciudad)
      },

      totalCertificados () {
        return this.alumnosFiltrados.length
      },

      totalPendientes () {
        return this.alumnosFiltrados.filter(alumno => alumno.estatus_operativo === 'PENDIENTE').length
      },

      totalExportados () {
        return this.alumnosFiltrados.filter(alumno => alumno.estatus_operativo === 'EXPORTADO').length
      },

      totalSeleccionados () {
        return this.selected.length
      },

      escuelaTexto () {
        return this.escuela === 1 ? 'INBI' : 'FAST'
      },

      tipoTexto () {
        const tipo = this.tiposCertificado.find(item => item.value === this.tipo)
        return tipo ? tipo.text : this.tipo
      },
    },

    watch: {
      tabEscuela () {
        this.buscarToken++
        this.tipo = 'fin_curso'
        this.ciclo = null
        this.alumnos = []
        this.selected = []
        this.ciudad = null
        this.edicionesLocales = {}
        this.getPlantillas()
      },

      tipo () {
        this.buscarToken++
        this.alumnos = []
        this.selected = []
        this.ciudad = null
        this.edicionesLocales = {}
        this.getPlantillas().then(() => {
          if(this.ciclo) this.buscar()
        })
      },

      ciclo (valor, anterior) {
        const idNuevo = valor ? valor.id_ciclo : null
        const idAnterior = anterior ? anterior.id_ciclo : null
        if(idNuevo === idAnterior) return

        this.selected = []
        this.edicionesLocales = {}
        if(valor){
          this.buscar()
          return
        }

        this.alumnos = []
      },

      plantillaSeleccionadaId () {
        this.cargarPlantillaForm()
      },

      ciudad () {
        this.selected = []
      },
    },

    created () {
      this.getCiclos()
      this.getPlantillas()
    },

    beforeDestroy () {
      this.buscarToken++
    },

    methods: {
      getCiclos () {
        this.cargar = true
        return this.$http.get('ciclos.list').then(response => {
          this.ciclos = response.data
            .filter(ciclo => ciclo.ciclo && ciclo.ciclo.search('CICLO') !== -1)
            .sort((a, b) => b.id_ciclo - a.id_ciclo)
        }).catch(error => {
          this.validarError(error)
        }).finally(() => {
          this.cargar = false
        })
      },

      certificadoKey (alumno) {
        return [
          alumno.escuela,
          alumno.tipo_certificado || this.tipo,
          alumno.iderp || alumno.id_alumno,
          alumno.id_plantel || 0,
          alumno.id_grupo || 0,
          alumno.matricula || alumno.matricula_erp || '',
        ].join('-')
      },

      aplicarEdicionAlumno (alumno) {
        const edicion = this.edicionesLocales[alumno.certificado_key]
        if(!edicion) return alumno

        return {
          ...alumno,
          nombre_modificado: edicion
        }
      },

      certificadoExportable (item) {
        const actual = this.alumnos.find(alumno => alumno.certificado_key === item.certificado_key) || item
        return this.aplicarEdicionAlumno(actual)
      },

      certificadosParaExportar (lista) {
        return lista.map(item => this.certificadoExportable(item))
      },

      edicionesParaApi (lista = null) {
        const keys = new Set(
          Array.isArray(lista) && lista.length
            ? lista.map(item => item.certificado_key).filter(Boolean)
            : Object.keys(this.edicionesLocales)
        )

        return [...keys].map(certificadoKey => {
          const alumno = this.alumnos.find(item => item.certificado_key === certificadoKey) || {}
          return {
            certificado_key: certificadoKey,
            iderp: alumno.iderp || alumno.id_alumno || null,
            matricula: alumno.matricula || alumno.matricula_erp || '',
            nombre_modificado: this.edicionesLocales[certificadoKey]
          }
        }).filter(edicion => edicion.nombre_modificado)
      },

      limpiarEdicionesLocalesObsoletas () {
        const keysActivas = new Set(this.alumnos.map(alumno => alumno.certificado_key))
        Object.keys(this.edicionesLocales).forEach(certificadoKey => {
          if(!keysActivas.has(certificadoKey)){
            this.$delete(this.edicionesLocales, certificadoKey)
          }
        })
      },

      buscar () {
        if(!this.ciclo){
          this.alumnos = []
          this.selected = []
          return Promise.resolve()
        }

        const token = ++this.buscarToken
        this.cargar = true
        this.selected = []

        const payload = {
          escuela: this.escuela,
          tipo: this.tipo,
          id_ciclo: this.ciclo.id_ciclo,
          id_ciclo_relacionado: this.ciclo.id_ciclo_relacionado || null
        }

        return this.$http.post('academico.certificados.operacion.alumnos', payload).then(response => {
          if(token !== this.buscarToken) return

          const lista = Array.isArray(response.data) ? response.data : []
          this.alumnos = lista.map(alumno => {
            const registro = {
              ...alumno,
              certificado_key: this.certificadoKey(alumno)
            }
            return this.aplicarEdicionAlumno(registro)
          })
          this.limpiarEdicionesLocalesObsoletas()
        }).catch(error => {
          if(token !== this.buscarToken) return
          this.validarError(error)
        }).finally(() => {
          if(token === this.buscarToken){
            this.cargar = false
          }
        })
      },

      generarLotePDF () {
        if(!this.selected.length){
          this.mostrarMensaje('Selecciona al menos un certificado', '#FF2C2C')
          return
        }

        this.generandoLote = true
        const certificados = this.certificadosParaExportar(this.selected)

        return this.$http.post('academico.certificados.operacion.lote', {
          certificados,
          ediciones: this.edicionesParaApi(certificados)
        }).then(response => {
          this.abrirPdfGenerado(response.data)
          this.mostrarMensaje('Lote PDF generado correctamente', 'success')
          this.buscar()
        }).catch(error => {
          this.validarError(error)
        }).finally(() => {
          this.generandoLote = false
        })
      },

      generarLoteCiudad () {
        if(!this.ciudad){
          this.mostrarMensaje('Selecciona una ciudad', '#FF2C2C')
          return
        }

        this.generandoCiudad = true

        return this.$http.post('academico.certificados.operacion.lote.ciudad', {
          escuela: this.escuela,
          tipo: this.tipo,
          id_ciclo: this.ciclo ? this.ciclo.id_ciclo : null,
          id_ciclo_relacionado: this.ciclo ? this.ciclo.id_ciclo_relacionado : null,
          ciudad: this.ciudad,
          ediciones: this.edicionesParaApi(this.alumnosFiltrados)
        }).then(response => {
          this.abrirPdfGenerado(response.data)
          this.mostrarMensaje('Lote por ciudad generado correctamente', 'success')
          this.buscar()
        }).catch(error => {
          this.validarError(error)
        }).finally(() => {
          this.generandoCiudad = false
        })
      },

      getPlantillas () {
        return this.$http.get('academico.certificados.operacion.plantillas', {
          params: {
            escuela: this.escuela,
            tipo: this.tipo
          }
        }).then(response => {
          this.plantillas = response.data || []
          this.plantillaSeleccionadaId = this.plantillas.length ? this.plantillas[0].id : null
          this.cargarPlantillaForm()
        }).catch(error => {
          this.validarError(error)
        })
      },

      cargarPlantillaForm () {
        const plantilla = this.plantillas.find(item => item.id === this.plantillaSeleccionadaId)
        if(!plantilla){
          this.plantillaForm = {
            nombre_y: null,
            nombre_font_size: null,
            firma_x: null,
            firma_y: null,
            firma_width: null,
            url: null,
            dinamica: false
          }
          return
        }

        this.plantillaForm = { ...plantilla }
        this.plantillaFile = null
      },

      guardarPlantilla () {
        const plantilla = this.plantillas.find(item => item.id === this.plantillaSeleccionadaId)
        if(!plantilla){
          this.mostrarMensaje('Selecciona una plantilla', '#FF2C2C')
          return
        }

        const formData = new FormData()
        formData.append('escuela', plantilla.escuela)
        formData.append('tipo_certificado', plantilla.tipo_certificado)
        formData.append('nivel', plantilla.nivel)
        formData.append('nombre_plantilla', plantilla.nombre_plantilla)
        formData.append('nombre_y', this.plantillaForm.nombre_y)
        formData.append('nombre_font_size', this.plantillaForm.nombre_font_size)
        formData.append('firma_x', this.plantillaForm.firma_x)
        formData.append('firma_y', this.plantillaForm.firma_y)
        formData.append('firma_width', this.plantillaForm.firma_width)

        if(this.plantillaFile){
          formData.append('file', this.plantillaFile)
        }

        this.guardandoPlantilla = true

        return this.$http.post('academico.certificados.operacion.plantillas', formData).then(() => {
          this.mostrarMensaje('Plantilla guardada correctamente', 'success')
          this.getPlantillas()
        }).catch(error => {
          this.validarError(error)
        }).finally(() => {
          this.guardandoPlantilla = false
        })
      },

      editarCertificado (item) {
        this.certificadoEditado = { ...item }
        this.dialogEditar = true
      },

      aplicarNombreLocal (nombre) {
        this.$set(this.edicionesLocales, this.certificadoEditado.certificado_key, nombre)

        const index = this.alumnos.findIndex(item => item.certificado_key === this.certificadoEditado.certificado_key)
        if(index !== -1){
          this.$set(this.alumnos, index, {
            ...this.alumnos[index],
            nombre_modificado: nombre,
            nombre_sistema: nombre
          })
        }

        this.selected = this.selected.map(item => {
          if(item.certificado_key !== this.certificadoEditado.certificado_key) return item
          return {
            ...this.certificadoExportable(item),
            nombre_modificado: nombre,
            nombre_sistema: nombre
          }
        })

        this.certificadoEditado = {
          ...this.certificadoEditado,
          nombre_modificado: nombre,
          nombre_sistema: nombre
        }
      },

      sincronizarNombreAlumno (nombre) {
        return this.$http.post('academico.certificados.operacion.nombre', {
          iderp: this.certificadoEditado.iderp || this.certificadoEditado.id_alumno,
          escuela: this.certificadoEditado.escuela || this.escuela,
          nombre_modificado: nombre,
          tipo_certificado: this.certificadoEditado.tipo_certificado || this.tipo,
          idcertificacion: this.certificadoEditado.idcertificacion || null,
          idcertificado_fisico: this.certificadoEditado.idcertificado_fisico || null,
          estatus: this.certificadoEditado.estatus || 0
        })
      },

      guardarEdicionIndividual () {
        const nombre = String(this.certificadoEditado.nombre_modificado || '').trim()
        if(!nombre){
          this.mostrarMensaje('Captura un nombre para imprimir', '#FF2C2C')
          return
        }

        this.guardandoNombre = true

        return this.sincronizarNombreAlumno(nombre).then(() => {
          this.aplicarNombreLocal(nombre)
          this.mostrarMensaje('Nombre actualizado en inscripciones', 'success')
          this.dialogEditar = false
        }).catch(error => {
          this.validarError(error)
        }).finally(() => {
          this.guardandoNombre = false
        })
      },

      exportarIndividual () {
        if(!this.certificadoEditado.certificado_key){
          return
        }

        const nombre = String(this.certificadoEditado.nombre_modificado || '').trim()
        if(!nombre){
          this.mostrarMensaje('Captura un nombre para imprimir', '#FF2C2C')
          return
        }

        this.$set(this.edicionesLocales, this.certificadoEditado.certificado_key, nombre)

        this.generandoIndividual = true

        return this.sincronizarNombreAlumno(nombre).then(() => {
          this.aplicarNombreLocal(nombre)

          const certificado = this.certificadoExportable({
            ...this.certificadoEditado,
            nombre_modificado: nombre,
            nombre_sistema: nombre
          })

          return this.$http.post('academico.certificados.operacion.lote', {
            certificados: [certificado],
            ediciones: this.edicionesParaApi([certificado])
          })
        }).then(response => {
          this.abrirPdfGenerado(response.data)
          this.mostrarMensaje('Certificado individual generado correctamente', 'success')
          this.dialogEditar = false
          this.buscar()
        }).catch(error => {
          this.validarError(error)
        }).finally(() => {
          this.generandoIndividual = false
        })
      },

      abrirPdfGenerado (data) {
        const archivo = data && data.archivo
        if(!archivo){
          this.mostrarMensaje('No se recibio el archivo generado', '#FF2C2C')
          return
        }

        this.validarMarcasMatricula(data)
        this.validarEstatusExportado(data)

        const baseUrl = this.$http.defaults.baseURL || ''
        window.open(`${baseUrl}pdfs/${archivo}`, '_blank')
      },

      validarMarcasMatricula (data) {
        const advertencias = Array.isArray(data && data.advertencias_matricula)
          ? data.advertencias_matricula
          : []

        if(!advertencias.length) return

        const total = advertencias.length
        this.mostrarMensaje(
          `${total} certificado(s) se generaron sin marca de matricula. Revisa sucursal y matricula del alumno.`,
          '#FF9800'
        )
      },

      validarEstatusExportado (data) {
        const errores = Array.isArray(data && data.errores_estatus)
          ? data.errores_estatus
          : []

        if(!errores.length) return

        this.mostrarMensaje(
          `${errores.length} certificado(s) no pudieron actualizar su estatus a exportado.`,
          '#FF9800'
        )
      },

      descargarListaNombres () {
        const lista = this.selected.length ? this.selected : this.alumnosFiltrados
        if(!lista.length){
          this.mostrarMensaje('No hay nombres para descargar', '#FF2C2C')
          return
        }

        const filas = lista.map(item => ({
          matricula: item.matricula || '',
          nombre_sistema: item.nombre_sistema || '',
          nombre_modificado: item.nombre_modificado || '',
          nombre_corregido: '',
          motivo: '',
          sucursal: item.sucursal || '',
          escuela: Number(item.escuela) === 2 ? 'FAST' : 'INBI',
          tipo: item.tipo_certificado || this.tipo,
        }))

        const hoja = XLSX.utils.json_to_sheet(filas)
        hoja['!cols'] = [
          { wch: 14 },
          { wch: 36 },
          { wch: 36 },
          { wch: 36 },
          { wch: 28 },
          { wch: 18 },
          { wch: 10 },
          { wch: 14 },
        ]

        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, hoja, 'Nombres')
        const tipo = String(this.tipo || 'certificados').replace(/_/g, '-')
        XLSX.writeFile(workbook, `nombres-certificados-${this.escuelaTexto}-${tipo}.xlsx`)

        this.mostrarMensaje(`Lista de ${lista.length} nombres descargada en Excel`, 'success')
      },

      urlArchivo (url) {
        if(!url) return ''
        if(String(url).search(/^https?:\/\//) === 0) return url
        const baseUrl = (this.$http.defaults.baseURL || '').replace(/\/$/, '')
        return `${baseUrl}${url}`
      },

      colorEstatus (estatus) {
        const colores = {
          PENDIENTE: 'red darken-2',
          EXPORTADO: 'deep-purple darken-3',
          ENVIADO: 'teal darken-2',
          RECIBIDO: 'orange darken-3',
          ENTREGADO: 'green darken-2',
        }

        return colores[estatus] || 'blue-grey'
      },

      validarError (error) {
        const body = error && (error.body || (error.response && error.response.data))
        this.mostrarMensaje(body && body.message ? body.message : 'Error al consultar certificados', '#FF2C2C')
      },

      mostrarMensaje (mensaje, color = 'primary') {
        this.parametros = {
          dialogError: true,
          mensaje,
          color
        }
        this.respuestaAlerta = true
      },
    },
  }
</script>

<style scoped>
.certificados-page {
  min-height: 100vh;
  padding-top: 14px !important;
  background:
    radial-gradient(circle at 8% 5%, rgba(46, 218, 213, 0.22), transparent 22rem),
    radial-gradient(circle at 88% 18%, rgba(88, 70, 246, 0.16), transparent 24rem),
    linear-gradient(180deg, #f8fbff 0%, #eef4fb 100%);
}

.cert-hero {
  position: relative;
  overflow: hidden;
  border-radius: 22px;
  padding: 16px 22px;
  color: #0f172a;
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.96), rgba(245, 250, 255, 0.96)),
    linear-gradient(90deg, rgba(0, 13, 74, 0.05), rgba(46, 218, 213, 0.08));
  border: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow: 0 14px 38px rgba(15, 23, 42, 0.08);
}

.hero-kicker {
  display: inline-flex;
  align-items: center;
  padding: 5px 10px;
  border-radius: 999px;
  color: #2563eb;
  background: rgba(37, 99, 235, 0.08);
  border: 1px solid rgba(37, 99, 235, 0.1);
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.hero-title {
  margin: 4px 0 1px;
  font-size: clamp(1.32rem, 1.8vw, 1.7rem);
  font-weight: 800;
  line-height: 1.08;
}

.hero-subtitle {
  max-width: 760px;
  color: #64748b;
  font-size: 0.8rem;
}

.hero-actions {
  position: relative;
  z-index: 1;
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
}

.hero-chip {
  color: #475569 !important;
  background: #f8fafc !important;
  border: 1px solid #e2e8f0;
  box-shadow: none;
}

.hero-refresh {
  color: #0f172a !important;
  font-weight: 700;
  text-transform: none;
  border: 1px solid #e2e8f0;
  box-shadow: none;
}

.cert-panel,
.table-shell,
.action-bar,
.template-panel .v-expansion-panel,
.edit-dialog {
  border-radius: 24px !important;
  border: 1px solid rgba(15, 23, 42, 0.06);
  box-shadow: 0 16px 46px rgba(15, 23, 42, 0.08) !important;
}

.cert-panel {
  overflow: hidden;
  background: rgba(255, 255, 255, 0.94) !important;
  backdrop-filter: blur(14px);
}

.school-tabs {
  padding: 6px 18px 0;
}

.school-tabs .v-tab {
  min-width: 190px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: none;
}

.filter-grid ::v-deep .v-input__slot,
.template-panel ::v-deep .v-input__slot,
.edit-dialog ::v-deep .v-input__slot {
  background: #fbfdff !important;
  border-color: rgba(15, 23, 42, 0.14) !important;
}

.filter-grid ::v-deep .v-input {
  font-size: 0.82rem;
}

.stats-strip {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px;
  border-radius: 18px;
  background: #f8fbff;
  border: 1px solid rgba(15, 23, 42, 0.06);
}

.metric-pill {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 34px;
  padding: 6px 10px;
  border-radius: 999px;
  background: white;
  border: 1px solid rgba(15, 23, 42, 0.07);
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.04);
}

.metric-pill span {
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
}

.metric-pill strong {
  color: #0f172a;
  font-size: 1rem;
  font-weight: 900;
  line-height: 1;
}

.metric-pill small,
.action-copy,
.panel-subtitle,
.table-subtitle {
  color: #64748b;
}

.metric-pending {
  background: #fff7f7;
}

.metric-exported {
  background: #f7f3ff;
}

.metric-selected {
  background: #ecfeff;
}

.modern-alert {
  border-radius: 18px;
  border: 1px solid rgba(3, 169, 244, 0.14);
}

.notice-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(360px, 0.72fr);
  gap: 10px;
  align-items: stretch;
}

.notice-item {
  min-height: 42px;
}

.modern-alert ::v-deep .v-alert__content {
  display: flex;
  align-items: center;
  font-size: 0.78rem;
  line-height: 1.25;
}

.action-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 42px;
  padding: 7px 12px;
  background: #eaf7ff;
  border-color: rgba(3, 169, 244, 0.14) !important;
  box-shadow: none !important;
}

.action-info {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  color: #1976a8;
  font-size: 0.78rem;
}

.action-info strong {
  color: #0f172a;
  font-size: 0.78rem;
}

.action-info span {
  color: #3383a9;
}

.action-buttons {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.panel-title,
.table-title,
.dialog-title {
  display: flex;
  align-items: center;
  color: #0f172a;
  font-weight: 800;
}

.action-copy,
.panel-subtitle,
.table-subtitle {
  font-size: 0.82rem;
}

.template-panel .v-expansion-panel {
  overflow: hidden;
}

.template-panel ::v-deep .v-expansion-panel-header {
  min-height: 78px;
  padding: 20px 24px;
  background:
    linear-gradient(135deg, #ffffff 0%, #f8fbff 100%),
    radial-gradient(circle at top right, rgba(33, 150, 243, 0.1), transparent 12rem);
}

.preview-card {
  padding: 14px;
  border-radius: 22px;
  background:
    linear-gradient(45deg, rgba(148, 163, 184, 0.14) 25%, transparent 25%),
    linear-gradient(-45deg, rgba(148, 163, 184, 0.14) 25%, transparent 25%),
    #ffffff;
  background-size: 22px 22px;
  border: 1px solid rgba(15, 23, 42, 0.08);
}

.status-chip {
  font-weight: 700;
}

.table-shell {
  overflow: hidden;
  background: white;
}

.table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 22px 12px;
}

.table-title {
  display: block;
  font-size: 1rem;
}

.table-subtitle {
  margin-top: 3px;
}

.cert-table {
  border-top: 1px solid #eef2f7;
}

.cert-table ::v-deep .v-data-table__wrapper {
  border-radius: 0 0 24px 24px;
}

.cert-table ::v-deep thead th {
  color: #64748b !important;
  font-size: 0.74rem !important;
  font-weight: 800 !important;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  background: #f8fafc;
}

.cert-table ::v-deep tbody tr {
  transition: background 0.18s ease, transform 0.18s ease;
}

.cert-table ::v-deep tbody tr:hover {
  background: #f8fbff !important;
}

.cert-table ::v-deep td {
  height: 50px !important;
  color: #1f2937;
  border-bottom: 1px solid #eef2f7 !important;
}

.cert-table ::v-deep .v-data-footer {
  border-top: 1px solid #eef2f7;
}

.dialog-title {
  padding: 24px 24px 12px;
}

@media (max-width: 960px) {
  .cert-hero {
    padding: 14px;
  }

  .table-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .action-bar {
    align-items: flex-start;
    flex-direction: column;
  }

  .notice-row {
    grid-template-columns: 1fr;
  }

  .action-buttons {
    width: 100%;
  }

  .action-buttons .v-btn {
    flex: 1;
  }

  .stats-strip {
    align-items: stretch;
    flex-direction: column;
  }

  .metric-pill {
    justify-content: space-between;
    width: 100%;
  }
}
</style>
